/**
 * EditorBreadcrumb — Shows current section hierarchy as user scrolls.
 * e.g. "Document > Section 2: Architecture > 2.3 Data Model"
 * Powered by IntersectionObserver on heading elements.
 */

import { useState, useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import type { BlockNoteEditor as BlockNoteEditorCore } from '@blocknote/core';

interface BreadcrumbSegment {
  id: string;
  level: number;
  text: string;
}

interface EditorBreadcrumbProps {
  editor: BlockNoteEditorCore | null;
  className?: string;
}

export function EditorBreadcrumb({ editor, className }: EditorBreadcrumbProps) {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbSegment[]>([]);
  const headingsRef = useRef<BreadcrumbSegment[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Build heading list from editor blocks
  useEffect(() => {
    if (!editor) return;

    const update = () => {
      headingsRef.current = editor.document
        .filter(block => block.type === 'heading')
        .map(block => ({
          id: block.id,
          level: (block.props as { level: number }).level,
          text: Array.isArray(block.content)
            ? (block.content as Array<{ type: string; text?: string }>)
                .filter(c => c.type === 'text')
                .map(c => c.text || '')
                .join('')
            : '',
        }))
        .filter(h => h.text.length > 0);
    };

    update();
    const unsubscribe = editor.onEditorContentChange(update);
    return () => unsubscribe?.();
  }, [editor]);

  // IntersectionObserver to track which heading the user is "in"
  useEffect(() => {
    if (!editor || headingsRef.current.length === 0) return;

    observerRef.current?.disconnect();

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const blockId = entry.target.getAttribute('data-id');
        if (!blockId) continue;

        const headings = headingsRef.current;
        const idx = headings.findIndex(h => h.id === blockId);
        if (idx === -1) continue;

        // Build breadcrumb chain: find parent headings of higher level
        const chain: BreadcrumbSegment[] = [];
        const current = headings[idx];
        chain.unshift(current);

        for (let i = idx - 1; i >= 0; i--) {
          if (headings[i].level < current.level && !chain.some(c => c.level === headings[i].level)) {
            chain.unshift(headings[i]);
          }
          if (chain[0].level === 1) break;
        }

        setBreadcrumbs(chain);
        break;
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '-10% 0px -80% 0px',
    });

    // Observe heading elements after a short delay (DOM needs to settle)
    const timer = setTimeout(() => {
      headingsRef.current.forEach(h => {
        const el = document.querySelector(`[data-id="${h.id}"]`);
        if (el) observer.observe(el);
      });
    }, 500);

    observerRef.current = observer;

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [editor]);

  if (breadcrumbs.length === 0) return null;

  return (
    <div className={`flex items-center gap-1 px-4 py-1.5 text-xs text-stone-500 bg-stone-50 border-b border-stone-100 overflow-x-auto ${className || ''}`}>
      {breadcrumbs.map((segment, i) => (
        <span key={segment.id} className="flex items-center gap-1 whitespace-nowrap">
          {i > 0 && <ChevronRight size={10} className="text-stone-300" />}
          <span
            className="hover:text-stone-700 cursor-pointer transition-colors"
            onClick={() => {
              const el = document.querySelector(`[data-id="${segment.id}"]`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            {segment.text}
          </span>
        </span>
      ))}
    </div>
  );
}

export default EditorBreadcrumb;
